// Owner Customers page (smoke-test fixes, 2026-05-20).
//
// Search-first: a search box (email / phone / card code — exact match) with
// a recent-customers list below it when the box is empty. Each row can
// "recover pass" — re-mint a wallet save link + QR so a customer who lost
// their phone or removed the pass can re-add it.

import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import {
  useCustomerSearch,
  useRecentCustomers,
  useResetCard,
  type CustomerRow,
  type CustomerSearchRow,
} from '../hooks/useCustomers.js'
import { CustomerDetailModal } from '../components/CustomerDetailModal.js'
import { RecoverPassModal } from '../../shared/recover-pass/RecoverPassModal.js'
import { OwnerApiError } from '../ownerApi.js'
import '../../staff/pages/staff.css'
import '../../shared/membership/membership.css'

// A row the list can render — the recent list carries email/phone, the
// search endpoint doesn't, so those are optional.
type ListRow = CustomerRow | CustomerSearchRow

function hasContact(r: ListRow): r is CustomerRow {
  return 'email' in r
}

// PH-local "joined" timestamp (UTC+8) — same wall-clock the owner reads on the
// Logs page (e.g. "Jun 12, 2:17 PM").
function formatJoined(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('en-PH', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

export function CustomersPage() {
  const [query, setQuery] = useState('')
  const recent = useRecentCustomers()
  const search = useCustomerSearch(query)
  const [exporting, setExporting] = useState(false)

  const trimmed = query.trim()
  const searching = trimmed.length >= 3
  const [recoverFor, setRecoverFor] = useState<ListRow | null>(null)
  // SLICE-MEMBERSHIP-PASS — opening a row's details fetches the card detail
  // (which carries `membership_context` for membership cards) so the owner can
  // manage the membership (tier / renew) without a separate scan.
  const [detailFor, setDetailFor] = useState<ListRow | null>(null)

  const rows: ListRow[] = useMemo(() => {
    if (searching) return search.data?.results ?? []
    return recent.data?.customers ?? []
  }, [searching, search.data, recent.data])

  // Plain fetch (not ownerApi) — the response is a CSV file, not JSON.
  // Cookie-authenticated same-origin request, downloaded via a throwaway
  // object URL rather than a raw <a href> navigation so a failure (expired
  // session, no active program) shows a toast instead of "downloading" a
  // JSON error body as a .csv file.
  async function downloadCsv() {
    setExporting(true)
    try {
      const res = await fetch('/api/owner/customers/export', {
        credentials: 'include',
      })
      if (!res.ok) {
        toast.error('Could not export customers. Try again.')
        return
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `customers-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Could not export customers. Try again.')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
        }}
      >
        <h1>Customers</h1>
        <button
          type="button"
          className="owner-btn owner-btn-sm"
          onClick={downloadCsv}
          disabled={exporting}
        >
          {exporting ? 'Exporting…' : 'Download CSV'}
        </button>
      </div>
      <p className="owner-greeting-sub">
        Find a customer to recover their loyalty pass — useful if they lost
        their phone or removed the pass by accident.
      </p>

      <div className="staff-enroll-field" style={{ marginTop: 16 }}>
        <label htmlFor="customer-search" className="stamp-label">
          Search by email or phone
        </label>
        <input
          id="customer-search"
          name="customer-search"
          autoComplete="off"
          className="owner-input"
          type="text"
          inputMode="text"
          placeholder="maria@example.com or 09171234567"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          spellCheck={false}
        />
        <p className="stamp-hint">
          First name isn’t searchable — it isn’t unique. Use the customer’s
          email, phone, or card code.
        </p>
      </div>

      <div style={{ marginTop: 18 }}>
        {!searching && (
          <h2 className="broadcast-section-heading">Recent customers</h2>
        )}

        {(searching ? search.isLoading : recent.isLoading) && (
          <p className="owner-loading">Loading…</p>
        )}

        {searching && search.isError && (
          <p className="owner-error">Search failed. Try again.</p>
        )}
        {!searching && recent.isError && (
          <p className="owner-error">Couldn’t load customers. Refresh.</p>
        )}

        {!((searching ? search.isLoading : recent.isLoading)) &&
          rows.length === 0 && (
            <div className="owner-card owner-empty-state">
              <p className="owner-card-title">
                {searching ? 'No match' : 'No customers yet'}
              </p>
              <p className="owner-card-sub">
                {searching
                  ? 'No customer with that email, phone, or card code.'
                  : 'Customers appear here once they enrol and add a pass.'}
              </p>
            </div>
          )}

        {rows.map((r) => (
          <CustomerRowCard
            key={r.card_id}
            row={r}
            onRecover={() => setRecoverFor(r)}
            onDetails={() => setDetailFor(r)}
          />
        ))}
      </div>

      {recoverFor && (
        <RecoverPassModal
          cardId={recoverFor.card_id}
          customerName={recoverFor.first_name}
          onClose={() => setRecoverFor(null)}
        />
      )}

      {detailFor && (
        <CustomerDetailModal
          row={detailFor}
          onClose={() => setDetailFor(null)}
          onRecover={() => {
            setRecoverFor(detailFor)
            setDetailFor(null)
          }}
        />
      )}
    </div>
  )
}

function CustomerRowCard({
  row,
  onRecover,
  onDetails,
}: {
  row: ListRow
  onRecover: () => void
  onDetails: () => void
}) {
  const name = row.first_name?.trim() || 'Customer'
  const contact = hasContact(row)
    ? row.email || row.phone || null
    : null
  const reset = useResetCard()

  async function handleReset() {
    if (
      !window.confirm(
        `Reset ${name}'s card? Their stamps go back to 0 and they start a fresh round on the same pass.`,
      )
    ) {
      return
    }
    try {
      await reset.mutateAsync(row.card_id)
      toast.success(`${name}'s card was reset to a fresh round.`)
    } catch (err) {
      const msg =
        err instanceof OwnerApiError
          ? err.userMessage
          : 'Could not reset the card.'
      toast.error(msg)
    }
  }

  return (
    <div className="owner-card owner-branch-row">
      <div style={{ minWidth: 0 }}>
        <div
          className="owner-card-title"
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}
        >
          {name}
          {row.status === 'completed' && (
            <span className="owner-pill owner-pill-completed">Completed</span>
          )}
          {row.is_erased && (
            <span className="owner-pill owner-pill-revoked">Data erased</span>
          )}
          {row.is_revoked && !row.is_erased && (
            <span className="owner-pill owner-pill-revoked">Revoked</span>
          )}
        </div>
        <p className="owner-card-sub" style={{ margin: 0 }}>
          {contact ? `${contact} · ` : ''}
          {row.card_code} · {row.stamps_count} / {row.total_stamps} stamps
        </p>
        <p
          className="owner-card-sub"
          style={{ margin: '2px 0 0', opacity: 0.7 }}
        >
          Joined {formatJoined(row.created_at)}
        </p>
      </div>
      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          minWidth: 200,
        }}
      >
        {row.status === 'completed' && (
          <button
            type="button"
            className="owner-btn owner-btn-sm"
            onClick={handleReset}
            disabled={reset.isPending}
            style={{ flex: '1 1 0', minWidth: 100, whiteSpace: 'nowrap' }}
          >
            {reset.isPending ? 'Resetting…' : 'Reset card'}
          </button>
        )}
        <button
          type="button"
          className="owner-btn owner-btn-sm"
          onClick={onDetails}
          style={{ flex: '1 1 0', minWidth: 100, whiteSpace: 'nowrap' }}
        >
          Details
        </button>
        {/* Customer lifecycle (2026-09-07) — an erased customer's PII is
            gone; recovering their pass doesn't make sense (and the server
            now rejects it with CUSTOMER_DATA_ERASED). Revoke is reversible,
            so a revoked-but-not-erased customer still gets the button. */}
        {!row.is_erased && (
          <button
            type="button"
            className="owner-btn owner-btn-sm"
            onClick={onRecover}
            style={{ flex: '1 1 0', minWidth: 100, whiteSpace: 'nowrap' }}
          >
            Recover pass
          </button>
        )}
      </div>
    </div>
  )
}
